import {defineStore} from "pinia";
import {computed, ref} from "vue";
import {useViewer} from './useViewer';
import type {DefaultDatumPlaneKind, Viewer} from '@puppycad/cad-core';

/** 默认基准面名称集合 */
export const DEFAULT_DATUM_PLANE_NAMES = new Set(['前视基准面', '上视基准面', '右视基准面']);

/**
 * 基准面只读摘要。
 * 说明：
 * 1. Plane Store 只依赖最小可见字段；
 * 2. 不直接依赖 cad-core 内部 DatumPlane 类。
 */
export interface DatumPlaneSummary {
    /** 基准面 ID。 */
    id: number;
    /** 基准面名称。 */
    name: string;
    //------ 默认基准面稳定身份 开始------
    /** 默认内置基准面稳定身份；用户创建的基准面为空。 */
    defaultPlaneKind?: DefaultDatumPlaneKind | null;
    //------ 默认基准面稳定身份 结束------
    /** 当前是否可见。 */
    isVisible(): boolean;
}

//------ 默认基准面稳定身份 开始------
/**
 * 判断基准面摘要是否携带稳定默认身份。
 *
 * @param plane 基准面摘要。
 * @returns 是否携带稳定默认身份。
 */
function hasStableDefaultDatumPlaneKind(plane: DatumPlaneSummary): boolean {
    return plane.defaultPlaneKind !== undefined && plane.defaultPlaneKind !== null;
}

/**
 * 判断单个基准面摘要是否代表默认三基准面之一。
 *
 * 设计说明：
 * 1. 新运行时优先使用 cad-core 的稳定默认身份；
 * 2. 名称判断仅作为旧测试数据或旧摘要对象的兼容兜底；
 * 3. 真实默认面重命名后仍能被识别为默认面，避免草图入口和显隐快照失效。
 *
 * @param plane 基准面摘要。
 * @returns 是否代表默认三基准面之一。
 */
export function isDefaultDatumPlaneSummary(plane: DatumPlaneSummary): boolean {
    return hasStableDefaultDatumPlaneKind(plane) || DEFAULT_DATUM_PLANE_NAMES.has(plane.name);
}

/**
 * 从基准面列表中筛选默认三基准面。
 *
 * @param planes 基准面摘要列表。
 * @returns 默认三基准面摘要列表。
 */
function filterDefaultDatumPlanes(planes: DatumPlaneSummary[]): DatumPlaneSummary[] {
    const hasStableDefaultPlanes = planes.some((plane) => hasStableDefaultDatumPlaneKind(plane));
    return planes.filter((plane) => (
        hasStableDefaultPlanes
            ? hasStableDefaultDatumPlaneKind(plane)
            : DEFAULT_DATUM_PLANE_NAMES.has(plane.name)
    ));
}
//------ 默认基准面稳定身份 结束------

/**
 * 基准面状态全局 Store。
 * 职责：管理基准面选中、可见性快照及临时预览状态。
 */
export const usePlaneStore = defineStore('plane', () => {
    const {getViewer} = useViewer();

    /**
     * 解析本次操作应使用的 Viewer。
     *
     * @param runtimeViewer 调用方显式传入的当前 Viewer。
     * @returns 可用 Viewer；不存在时返回 null。
     */
    function resolveViewer(runtimeViewer?: Viewer | null): Viewer | null {
        // workflow 已经持有当前 Viewer 时必须优先使用它，避免全局引用滞后导致基准面显示无效。
        return runtimeViewer ?? getViewer();
    }

    /** 当前草图绘制所关联的基准面 ID，-1 表示未选择 */
    const currentDrawingDatumPlaneId = ref<number>(-1);

    /** 当前选中的基准面 ID，-1 表示未选中 */
    const selectedDatumPlaneId = ref<number>(-1);

    /** 是否已选择草图基准面 */
    const isSketchPlaneSelected = computed(() => currentDrawingDatumPlaneId.value !== -1);

    /** 进入草图前记录的默认基准面可见性快照 */
    const sketchDefaultPlaneSnapshot = ref<Record<number, boolean> | null>(null);

    /** 进入草图前记录的当前基准面可见性备份 */
    const sketchPlaneVisibilityBackup = ref<{ planeId: number; wasVisible: boolean } | null>(null);

    /**
     * 获取默认基准面列表。
     *
     * @param runtimeViewer 调用方显式传入的当前 Viewer。
     * @returns 默认基准面数组。
     */
    function getDefaultDatumPlanes(runtimeViewer?: Viewer | null): DatumPlaneSummary[] {
        return filterDefaultDatumPlanes(resolveViewer(runtimeViewer)?.listDatumPlaneSummaries?.() ?? []);
    }

    /**
     * 记录默认基准面可见性快照。
     * 仅在尚未记录时生效，防止覆盖之前的快照。
     *
     * @param runtimeViewer 调用方显式传入的当前 Viewer。
     */
    function captureDefaultPlaneSnapshot(runtimeViewer?: Viewer | null): void {
        if (sketchDefaultPlaneSnapshot.value) {
            return;
        }
        const snapshot: Record<number, boolean> = {};
        getDefaultDatumPlanes(runtimeViewer).forEach((plane) => {
            snapshot[plane.id] = plane.isVisible();
        });
        sketchDefaultPlaneSnapshot.value = snapshot;
    }

    /**
     * 显示全部默认基准面。
     *
     * @param runtimeViewer 调用方显式传入的当前 Viewer。
     * @returns 成功匹配到的默认基准面数量。
     */
    function showDefaultDatumPlanes(runtimeViewer?: Viewer | null): number {
        const viewer = resolveViewer(runtimeViewer);
        const defaultPlanes = getDefaultDatumPlanes(viewer);
        defaultPlanes.forEach((plane) => {
            viewer?.setDatumPlaneVisible?.(plane.id, true);
        });
        // 一组基准面显隐合并到宿主帧，不在草图入口强制执行渲染管线。
        viewer?.requestRender();
        return defaultPlanes.length;
    }

    /**
     * 隐藏默认前视、上视和右视基准面。
     *
     * 设计说明：
     * 1. 进入草图只需要收起默认选择辅助面，用户创建的正式基准面必须保持自身显示状态；
     * 2. 默认面识别复用稳定 `DefaultDatumPlaneKind`，仅对旧摘要回退名称判断；
     * 3. 禁止调用 Viewer 的全量隐藏入口后再恢复用户面，避免显隐闪烁和状态覆盖。
     *
     * @param runtimeViewer 调用方显式传入的当前 Viewer。
     * @returns 成功匹配到的默认基准面数量。
     */
    function hideDefaultDatumPlanes(runtimeViewer?: Viewer | null): number {
        const viewer = resolveViewer(runtimeViewer);
        const defaultPlanes = getDefaultDatumPlanes(viewer);
        defaultPlanes.forEach((plane) => {
            viewer?.setDatumPlaneVisible?.(plane.id, false);
        });
        // 隐藏默认辅助面只登记最终显示状态，与草图预览共享同一帧。
        viewer?.requestRender();
        return defaultPlanes.length;
    }

    /**
     * 恢复默认基准面快照中记录的可见性。
     * @param keepPlaneId - 排除恢复的基准面 ID。
     * @param runtimeViewer - 调用方显式传入的当前 Viewer。
     */
    function restoreDefaultPlaneSnapshot(keepPlaneId?: number, runtimeViewer?: Viewer | null): void {
        if (!sketchDefaultPlaneSnapshot.value) {
            return;
        }
        const viewer = resolveViewer(runtimeViewer);
        Object.entries(sketchDefaultPlaneSnapshot.value).forEach(([id, wasVisible]) => {
            const numericId = Number(id);
            if (keepPlaneId !== undefined && numericId === keepPlaneId) {
                return;
            }
            viewer?.setDatumPlaneVisible?.(numericId, wasVisible);
        });
        // 快照恢复完成后统一登记重绘，不逐面提交画面。
        viewer?.requestRender();
    }

    /**
     * 清除默认基准面快照。
     */
    function clearDefaultPlaneSnapshot(): void {
        sketchDefaultPlaneSnapshot.value = null;
    }

    /**
     * 记录进入草图时指定基准面的可见性，用于退出时恢复。
     * @param planeId - 基准面 ID。
     * @param runtimeViewer - 调用方显式传入的当前 Viewer。
     */
    function rememberSketchPlaneVisibility(planeId: number, runtimeViewer?: Viewer | null): void {
        if (sketchPlaneVisibilityBackup.value) {
            return;
        }
        const plane = resolveViewer(runtimeViewer)?.getDatumPlaneSummary?.(planeId);
        if (!plane) {
            return;
        }
        sketchPlaneVisibilityBackup.value = {
            planeId,
            wasVisible: plane.isVisible(),
        };
    }

    /**
     * 恢复进入草图前的基准面可见性。
     *
     * @param runtimeViewer 调用方显式传入的当前 Viewer。
     */
    function restoreSketchPlaneVisibility(runtimeViewer?: Viewer | null): void {
        const backup = sketchPlaneVisibilityBackup.value;
        if (!backup) {
            return;
        }
        const viewer = resolveViewer(runtimeViewer);
        viewer?.setDatumPlaneVisible?.(backup.planeId, backup.wasVisible);
        // 恢复进入草图前的显示状态，同样由按需帧调度器消费。
        viewer?.requestRender();
        sketchPlaneVisibilityBackup.value = null;
    }

    /**
     * 隐藏当前绘图基准面的临时预览。
     *
     * @param runtimeViewer 调用方显式传入的当前 Viewer。
     */
    function hideActiveDatumPlanePreview(runtimeViewer?: Viewer | null): void {
        if (currentDrawingDatumPlaneId.value === -1) {
            return;
        }
        resolveViewer(runtimeViewer)?.hideTemporaryDatumPlane?.(currentDrawingDatumPlaneId.value);
    }

    /**
     * 清除选中基准面的临时预览并重置选中状态。
     *
     * @param runtimeViewer 调用方显式传入的当前 Viewer。
     */
    function clearSelectedDatumPlanePreview(runtimeViewer?: Viewer | null): void {
        if (selectedDatumPlaneId.value === -1) {
            return;
        }
        if (selectedDatumPlaneId.value === currentDrawingDatumPlaneId.value) {
            return;
        }
        resolveViewer(runtimeViewer)?.hideTemporaryDatumPlane?.(selectedDatumPlaneId.value);
        selectedDatumPlaneId.value = -1;
    }

    /**
     * 重置草图基准面运行时状态。
     *
     * @param runtimeViewer 调用方显式传入的当前 Viewer。
     */
    function resetPlaneRuntimeState(runtimeViewer?: Viewer | null): void {
        // 先恢复可见性，再清空 store 状态，避免新项目继承上一草图的隐藏/预览快照。
        restoreSketchPlaneVisibility(runtimeViewer);
        restoreDefaultPlaneSnapshot(undefined, runtimeViewer);
        clearSelectedDatumPlanePreview(runtimeViewer);
        clearDefaultPlaneSnapshot();
        currentDrawingDatumPlaneId.value = -1;
        selectedDatumPlaneId.value = -1;
        sketchPlaneVisibilityBackup.value = null;
    }

    return {
        currentDrawingDatumPlaneId,
        selectedDatumPlaneId,
        isSketchPlaneSelected,
        getDefaultDatumPlanes,
        captureDefaultPlaneSnapshot,
        showDefaultDatumPlanes,
        hideDefaultDatumPlanes,
        restoreDefaultPlaneSnapshot,
        clearDefaultPlaneSnapshot,
        rememberSketchPlaneVisibility,
        restoreSketchPlaneVisibility,
        hideActiveDatumPlanePreview,
        clearSelectedDatumPlanePreview,
        resetPlaneRuntimeState,
    };
})
